'use client';

import { createContext, useContext, type ReactNode } from 'react';

export const DRAWER_MENU_ROOT_PANEL = 'root';

export type DrawerMenuPanelEntry = {
  id: string;
  title?: ReactNode;
};

export type DrawerMenuContextValue = {
  /** Closes the sheet, reporting the close as an item pick. */
  closeFromItem: () => void;
  /** Root first, the panel on screen last. */
  stack: DrawerMenuPanelEntry[];
  pushPanel: (entry: DrawerMenuPanelEntry) => void;
  popPanel: () => void;
  /** Panel sliding out, null while idle. */
  outgoingPanel: string | null;
  endTransition: () => void;
};

const DrawerMenuContext = createContext<DrawerMenuContextValue | null>(null);

export const DrawerMenuProvider = DrawerMenuContext.Provider;

export const useDrawerMenuContext = () => {
  const context = useContext(DrawerMenuContext);

  if (!context) {
    throw new Error('DrawerMenu parts must be rendered inside a DrawerMenu.');
  }

  return context;
};

export type DrawerMenuPanelState = {
  isSubmenu: boolean;
  /** 0 on the root, 1 in a submenu, 2 in a submenu of a submenu. */
  depth: number;
  /** Title of the submenu on screen. Undefined on the root, which the header titles itself. */
  title?: ReactNode;
  /** Returns to the previous panel. Does nothing on the root. */
  back: () => void;
};

/**
 * The panel on screen. A custom header reads it to decide what each level shows:
 * its title, a Back button, a different look per depth.
 */
export const useDrawerMenuPanel = (): DrawerMenuPanelState => {
  const { stack, popPanel } = useDrawerMenuContext();
  const depth = stack.length - 1;
  const isSubmenu = depth > 0;

  return {
    isSubmenu,
    depth,
    title: isSubmenu ? stack[stack.length - 1]?.title : undefined,
    back: popPanel,
  };
};

export type DrawerMenuPanelsContextValue = {
  /**
   * Node every panel portals into. A submenu is declared inside the row that opens it,
   * so it needs a target above the rows to render as a sibling panel.
   */
  viewport: HTMLElement | null;
  reportHeight: (id: string, height: number) => void;
};

const DrawerMenuPanelsContext = createContext<DrawerMenuPanelsContextValue>({
  viewport: null,
  reportHeight: () => {},
});

export const DrawerMenuPanelsProvider = DrawerMenuPanelsContext.Provider;

export const useDrawerMenuPanelsContext = () => useContext(DrawerMenuPanelsContext);
